import {
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  DialogContentText,
  Grid,
  Box,
  Link,
} from '@nextiva/next-ui-components';
import React, { useState } from 'react';
import { useDeleteEnablementInfo } from '../WizardContextV3/useDeleteEnablementInfo';
import { Body2Typo } from '../WizardV3.styled';
import { ButtonStyled, StyledDialog } from './CustomDialog.styled';

const dataTestId = 'PlatformAdminUI-CustomDialog';
interface CustomDialogProps {
  handleButton1: () => void;
  handleButton2: () => void;
  open: boolean;
  buttonName1: string;
  buttonName2: string;
  title: string;
  content: string | React.ReactElement;
  button2Color: 'primary' | 'danger';
}

function CustomDialog({
  handleButton1,
  handleButton2,
  open,
  buttonName1,
  buttonName2,
  title,
  content,
  button2Color = 'primary',
}: CustomDialogProps) {
  return (
    <StyledDialog open={open} onClose={handleButton1} data-testid={dataTestId}>
      <DialogTitle style={{ padding: 0 }} variant="h5" color="#0B1029">
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography variant="body2" color="#54687F">
            {content}
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <ButtonStyled
          data-testid={`${dataTestId}-Button1`}
          color="secondary"
          onClick={handleButton1}
          variant="contained"
        >
          {buttonName1}
        </ButtonStyled>
        <ButtonStyled
          data-testid={`${dataTestId}-Button2`}
          color={button2Color}
          onClick={handleButton2}
          variant="contained"
        >
          {buttonName2}
        </ButtonStyled>
      </DialogActions>
    </StyledDialog>
  );
}

export const CompanyTypeDialogButton = (props: {
  disable: boolean;
  handleResetForm: () => void;
}) => {
  const { disable, handleResetForm } = props;
  const [open, setOpen] = useState(false);
  const deleteEnablementInfo = useDeleteEnablementInfo();

  const handleOpen = () => {
    setOpen(true);
  };
  const handleCancel = () => {
    setOpen(false);
  };

  const handleChange = () => {
    deleteEnablementInfo.mutate(null, {
      onSuccess: handleResetForm,
    });
  };

  return (
    <Grid item xs={1}>
      <Button
        variant="text"
        size="medium"
        color="primary"
        disabled={disable}
        data-testid={`${dataTestId}-ChangeCompanyTypeButton`}
        onClick={handleOpen}
      >
        Edit
      </Button>
      <CustomDialog
        handleButton1={handleCancel}
        handleButton2={handleChange}
        open={open}
        buttonName1="Cancel"
        buttonName2="Change"
        button2Color="danger"
        title="Change company type"
        content="If you change company type, you may loose some of the saved data. Are you sure you want to change company type?"
      />
    </Grid>
  );
};

interface SolePropDialogProps {
  handleCancel: () => void;
  handleAccept: () => void;
  openDialog: boolean;
}

export const SolePropDialog = ({
  handleCancel,
  handleAccept,
  openDialog,
}: SolePropDialogProps) => {
  return (
    <CustomDialog
      handleButton1={handleCancel}
      handleButton2={handleAccept}
      open={openDialog}
      buttonName1="Cancel"
      buttonName2="Accept"
      button2Color="primary"
      title="Sole proprietor business messaging restrictions"
      content={
        <>
          <Box component="p" sx={{ mb: 1 }}>
            <strong>ATTENTION: </strong>Sole Proprietor, in terms of Business
            messaging, is an individual or small business that{' '}
            <strong>DOES NOT</strong> have a corporate tax ID or EIN. If your
            business has a tax ID or EIN, choose
            <strong> Private Company</strong>.
          </Box>
          <strong>Please note:</strong>
          <Box component="ul" sx={{ 'list-style-position': 'inside', ml: 1 }}>
            <li>Only one phone number can be used for business messaging.</li>
            <li>
              You can send/receive 1000 messages per day and 15 messages per
              minute.
            </li>
            <li>
              You must pass a two-step validation process, which includes data
              validation and one-time password phone verification.
            </li>
          </Box>
        </>
      }
    />
  );
};

interface DeleteRegistrationProps {
  handleCancel: () => void;
  handleAccept: () => void;
  openDialog: boolean;
}

export const DeleteRegistrationDialog = ({
  handleCancel,
  handleAccept,
  openDialog,
}: DeleteRegistrationProps) => {
  return (
    <CustomDialog
      handleButton1={handleCancel}
      handleButton2={handleAccept}
      open={openDialog}
      buttonName1="Cancel"
      buttonName2="Delete"
      button2Color="danger"
      title="Delete registration in-progress"
      content="Are you sure you want to delete the in-progress registration form? All saved data will be lost."
    />
  );
};

interface ComplianceFeesDialogProps {
  handleAccept: () => void;
  handleCancel: () => void;
  openDialog: boolean;
}

const ContentComplianceFeesDialog = () => {
  return (
    <div>
      <Box sx={{ mb: 2 }}>
        <Body2Typo>
          Business messaging registration incurs fees from third party entities
          that charge various fees to manage registrations and protect
          consumers.
        </Body2Typo>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Body2Typo>
          One-time and recurring business messaging fee costs vary based on
          provided company type and business messaging intended use. Incurred
          fees will include one-time registration setup fees, monthly
          maintenance fees, and any other third-party fees incurred from
          regulation or misuse of business messaging. Most customers should at
          minimum expect $19 in one-time fees and $1.50 to $10 per month in
          recurring fees.
        </Body2Typo>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Body2Typo>
          Visit{' '}
          <Link
            href="https://help.nextiva.com/s/article/Business-Messaging-10DLC-Compliance-Fees"
            rel="noreferrer"
            target="_blank"
            data-testid={`${dataTestId}-OurWebsite`}
            sx={{ color: 'info.main', fontWeight: 500 }}
          >
            our website
          </Link>{' '}
          for more information and fee details.
        </Body2Typo>
      </Box>
      <Box>
        <Body2Typo>
          By clicking accept, you agree to pay invoiced Business Messaging fees.
        </Body2Typo>
      </Box>
    </div>
  );
};

export const ComplianceFeesDialog = ({
  handleAccept,
  handleCancel,
  openDialog,
}: ComplianceFeesDialogProps) => {
  return (
    <CustomDialog
      handleButton1={handleCancel}
      handleButton2={handleAccept}
      open={openDialog}
      buttonName1="Cancel"
      buttonName2="Accept"
      button2Color="primary"
      title="Important: Business messaging fees (one-time and recurring)"
      content={<ContentComplianceFeesDialog />}
    />
  );
};
